import { Button, ButtonIcon } from '@components/index'
import { ITemplate } from '@interfaces/tables/tableSales'
import React from 'react'
import { FieldValues, Path } from 'react-hook-form'
import { FiDownload } from 'react-icons/fi'
import { TiDelete } from 'react-icons/ti'
import { Params } from 'react-router-dom'
import { toast } from 'react-toastify'

interface IInputTemplate<T extends FieldValues> {
    acceptFiles: string
    onChange: (value: File | null) => void
    hasTemplate?: boolean
    getTemplate: () => Promise<ITemplate | undefined>
    removeTemplate: (remove: boolean) => void
    name?: Path<T>
    style?: object
    params?: Readonly<Params<string>>
}

export default function InputTemplate<T extends FieldValues>(props: IInputTemplate<T>): React.ReactElement {
    const handleFile = React.useRef<HTMLInputElement | null>(null)
    const handleClick = React.useCallback(() => {
        handleFile.current?.click()
    }, [])

    const handleDelete = React.useCallback(() => {
        if (handleFile.current) handleFile.current.value = ''
        props.onChange(null)
        setFile(null)
        props.removeTemplate(true)
    }, [])

    const [file, setFile] = React.useState<File | string | null>(null)
    const [hasTemplate, setHasTemplate] = React.useState(false)

    function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        if (e.target.files !== null) {
            const file = e.target.files[0]

            const fileAccepted = props.acceptFiles.includes(file.type)
            if (!fileAccepted) {
                toast('Formato de arquivo inválido', { type: 'error' })
                throw new Error('Formato de arquivo inválido')
            }

            setFile(file)
            props.onChange(file)
            setHasTemplate(true)

            if (props.removeTemplate)
                props.removeTemplate(false)
        }
    }

    async function downloadTemplate(): Promise<void> {
        const response = await props.getTemplate()
        if (response) {
            const blob = new Blob([response.template], { type: 'text/x-handlebars-template' })
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            const fileName = 'template.hbs'
            link.download = fileName
            link.click()
            link.parentNode?.removeChild(link)
        }
    }

    React.useEffect(() => {
        if (props.hasTemplate)
            setHasTemplate(props.hasTemplate)
    }, [props.hasTemplate])

    return (
        <>
            <input
                type='file'
                ref={handleFile}
                onChange={handleChange}
                accept={props.acceptFiles}
                style={{ display: 'none' }}
            />
            {props.acceptFiles === '.hbs' &&
                <div className='box-hbs'>
                    <Button
                        onClick={handleClick}
                        type='button'
                        title='Input de arquivos'
                        placeholder={hasTemplate ? 'Arquivo Selecionado...' : 'Selecionar arquivo'}
                        isDisabled={hasTemplate}
                    />
                    {hasTemplate &&
                        <>
                            {props.params?.id && !file &&
                                <ButtonIcon
                                    type='button'
                                    onClick={() => downloadTemplate()}
                                    title='Download do arquivo'
                                    style={{ backgroundColor: '#fff', padding: '0.6rem' }}
                                >
                                    <FiDownload color='#707070' />
                                </ButtonIcon>
                            }
                            <ButtonIcon
                                type='button'
                                onClick={() => {
                                    setHasTemplate(false)
                                    handleDelete()
                                }}
                                title='Remover Arquivo'
                                style={{ backgroundColor: '#fff', padding: '0.3rem' }}
                            >
                                <TiDelete size={25} />
                            </ButtonIcon>
                        </>
                    }
                </div>
            }
        </>
    )
}
